import { FC } from 'react'
import { platformLabel } from 'src/charts/ContentAnalysisV2/shared'
import { SentimentBar } from 'src/charts/TopicsSubtopics/SentimentBar'
import { SentimentHoverCard } from 'src/charts/TopicsSubtopics/SentimentHoverCard'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { barFillRatio } from 'src/interfaces/topics.interface'
import { campaignColor } from 'src/pages/Campaigns/CampaignComparison/MultiComparison/palette'
import { cn } from 'src/utils/cn'
import styles from './comparativeOverview.module.scss'
import { ContentCampaign, OverviewCard } from '../interfaces'

interface ComparativeOverviewProps {
  campaigns: ContentCampaign[]
  cards: OverviewCard[]
  periodLabel: string
}

const COPY = {
  campaigns: 'campaigns',
  categories: 'Top categories',
  contentType: 'Top content type',
  highest: 'Highest Volume',
  lowest: 'Lowest Volume',
  negative: 'negative',
  score: 'Sentiment score',
  sentiment: 'Sentiment',
  takeaway: 'Key takeaway',
  title: 'Comparative Overview',
  topDay: 'Top day',
  topPlatform: 'Top platform',
  topTopics: 'Top topics',
  totalPosts: 'Total posts',
  videoStatic: 'Video / Static',
}

// The score's 60 / 40 band, in the same three colours as the label.
const BAND_CLASS = {
  Neutral: 'sentimentNeutral',
  Poor: 'sentimentNegative',
  Positive: 'sentimentPositive',
} as const

const CATEGORY_LIMIT = 3

const formatErr = (err: number) =>
  `${Number.isInteger(err) ? err : err.toFixed(1)}%`

const SENTIMENT_CLASS = {
  Negative: 'sentimentNegative',
  Neutral: 'sentimentNeutral',
  Positive: 'sentimentPositive',
} as const

/*
 * Comparative Overview: the report's summary, a column per campaign.
 *
 * Last on the page and deliberately repetitive — everything here is stated by
 * a widget above it. It exists so a reader who scrolled past can leave with
 * the four campaigns side by side in one glance.
 */
export const ComparativeOverview: FC<ComparativeOverviewProps> = ({
  campaigns,
  cards,
  periodLabel,
}) => (
  <section className={styles.card}>
    <div className={styles.headerText}>
      <h3 className={styles.title}>{COPY.title}</h3>
      <p className={styles.subtitle}>
        {`${periodLabel} · ${campaigns.length} ${COPY.campaigns}`}
      </p>
    </div>

    <div className={styles.grid}>
      {campaigns.map((campaign, index) => {
        const card = cards.find((item) => item.campaignId === campaign.id)

        if (!card) return null

        return (
          <article className={styles.column} key={campaign.id}>
            <header className={styles.columnHead}>
              <span
                className={styles.dot}
                style={{ background: campaignColor(index) }}
              />

              <TruncatedTooltip
                className={styles.name}
                maxLines={2}
                text={campaign.name}
              />

              {/* A card at zero is not badged: "lowest volume" reads as the
                  quietest of the field, and an analysis that published nothing
                  did not take part in it. */}
              {card.badge && card.totalPosts > 0 && (
                <span
                  className={cn(
                    styles.badge,
                    card.badge === 'highest'
                      ? styles.badgeHighest
                      : styles.badgeLowest
                  )}
                >
                  {card.badge === 'highest' ? COPY.highest : COPY.lowest}
                </span>
              )}
            </header>

            <div className={styles.statRow}>
              <span className={styles.statLabel}>{COPY.totalPosts}</span>
              <span className={styles.statValue}>
                {card.totalPosts.toLocaleString('en-US')}
              </span>
            </div>

            <div className={styles.statRow}>
              <span className={styles.statLabel}>{COPY.videoStatic}</span>

              <span className={styles.split}>
                <span className={styles.splitBar}>
                  <span
                    className={styles.splitVideo}
                    style={{ width: `${card.videoPct}%` }}
                  />
                  <span
                    className={styles.splitStatic}
                    style={{ width: `${card.staticPct}%` }}
                  />
                </span>

                <span className={styles.splitValue}>
                  {`${card.videoPct}% / ${card.staticPct}%`}
                </span>
              </span>
            </div>

            <div className={styles.statRow}>
              <span className={styles.statLabel}>{COPY.sentiment}</span>
              <span
                className={cn(
                  styles.badge,
                  styles[SENTIMENT_CLASS[card.sentiment]]
                )}
              >
                {card.sentiment}
              </span>
            </div>

            {typeof card.sentimentScore === 'number' && (
              <div className={styles.statRow}>
                <span className={styles.statLabel}>{COPY.score}</span>
                <span className={styles.score}>
                  <span className={styles.scoreValue}>
                    {Math.round(card.sentimentScore)}
                    <span className={styles.scoreOutOf}>/100</span>
                  </span>
                  {card.sentimentScoreLabel && (
                    <span
                      className={cn(
                        styles.badge,
                        styles[BAND_CLASS[card.sentimentScoreLabel]]
                      )}
                    >
                      {card.sentimentScoreLabel}
                    </span>
                  )}
                </span>
              </div>
            )}

            {card.topDay !== undefined && (
              <div className={styles.statRow}>
                <span className={styles.statLabel}>{COPY.topDay}</span>
                <span className={styles.statText}>
                  {card.topDay?.label ?? '–'}
                </span>
              </div>
            )}

            {card.topPlatform !== undefined && (
              <div className={styles.statRow}>
                <span className={styles.statLabel}>{COPY.topPlatform}</span>
                <span className={styles.statText}>
                  {card.topPlatform
                    ? platformLabel(card.topPlatform.most.platform)
                    : '–'}
                </span>
              </div>
            )}

            {card.topContentType !== undefined && (
              <div className={styles.statRow}>
                <span className={styles.statLabel}>{COPY.contentType}</span>
                <span className={styles.statText}>
                  {card.topContentType
                    ? `${card.topContentType.label} · ERR ${formatErr(
                        card.topContentType.err
                      )}`
                    : '–'}
                </span>
              </div>
            )}

            {!!card.topTopics?.length && (
              <div className={styles.list}>
                <p className={styles.listTitle}>{COPY.topTopics}</p>
                {card.topTopics.map((topic) => (
                  <div className={styles.listRow} key={topic.name}>
                    <span className={styles.listName} title={topic.name}>
                      {topic.name}
                    </span>
                    <span className={styles.listCount}>
                      {topic.count.toLocaleString('en-US')}
                    </span>
                    <SentimentHoverCard
                      className={styles.listBar}
                      sentiment={topic.sentiment}
                      total={topic.count}
                      totalLabel="Total Comments"
                    >
                      <SentimentBar
                        fill={barFillRatio(
                          topic.count,
                          card.topTopics![0]?.count ?? 0
                        )}
                        flat
                        sentiment={topic.sentiment}
                      />
                    </SentimentHoverCard>
                  </div>
                ))}
              </div>
            )}

            {!!card.categories?.length && (
              <div className={styles.list}>
                <p className={styles.listTitle}>{COPY.categories}</p>
                {card.categories.slice(0, CATEGORY_LIMIT).map((category) => (
                  <div className={styles.listRow} key={category.name}>
                    <span className={styles.listName} title={category.name}>
                      {category.name}
                    </span>
                    <span className={styles.listCount}>
                      {category.count.toLocaleString('en-US')}
                    </span>
                    <span
                      className={cn(
                        styles.listNegative,
                        category.negative === 0 && styles.listNegativeNone
                      )}
                    >
                      {`${category.negative.toLocaleString('en-US')} ${
                        COPY.negative
                      }`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p className={styles.takeawayTitle}>{COPY.takeaway}</p>
            <p className={styles.takeaway}>{card.takeaway}</p>
          </article>
        )
      })}
    </div>
  </section>
)
